import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : "/area");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 bg-white flex-col justify-between p-12 border-r border-slate-100">
        <div>
          <img src="/studio-castelli-logo.jpg" alt="Studio Castelli" className="w-52 h-auto" data-testid="login-logo" />
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight text-navy">
            Area Riservata
            <br />
            <span className="text-[#3C763D]">Modelli F24</span>
          </h1>
          <p className="text-slate-600 max-w-md leading-relaxed">
            Visualizza i tuoi Modelli F24, scarica i documenti in PDF e autorizza il pagamento
            in totale sicurezza.
          </p>
          <div className="flex gap-8 pt-2">
            <div>
              <p className="font-display text-3xl font-bold text-[#3C763D]">25+</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Anni di esperienza</p>
            </div>
            <div>
              <p className="font-display text-3xl font-bold text-[#3C763D]">100%</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Tracciabilità</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          © Studio Castelli - Messina · P.IVA 02052340839
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-10">
            <img src="/studio-castelli-logo.jpg" alt="Studio Castelli" className="w-44 h-auto" />
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Accesso clienti</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy tracking-tight mb-2">
            Bentornato
          </h2>
          <p className="text-muted-foreground text-sm mb-8">
            Inserisci le credenziali fornite dallo studio per accedere alla tua area riservata.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.it"
                  className="pl-10 h-11"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-11"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error-message"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-navy hover:bg-navy-light text-white font-semibold transition-colors duration-200"
            >
              {loading ? "Accesso in corso..." : "Accedi all'Area Riservata"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-sm text-navy/70 hover:text-gold transition-colors duration-200"
              >
                Password dimenticata?
              </Link>
            </div>
          </form>

          <p className="text-xs text-muted-foreground mt-10 text-center">
            Non hai ancora le credenziali? Contatta lo studio:{" "}
            <a href="mailto:info@studiocastelli.it" className="text-navy font-medium hover:text-gold">
              info@studiocastelli.it
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
